import {
  useLazyQuery,
  useMutation,
  useQuery,
  useSubscription,
} from "@apollo/client";
import {
  faBan,
  faCar,
  faCircleExclamation,
  faDog,
  faExclamationTriangle,
  faEye,
  faFaucet,
  faFire,
  faPeopleGroup,
  faPerson,
  faPoop,
  faRotate,
  faUtensils,
  faWheelchair,
} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  Alert,
  Button,
  Center,
  Checkbox,
  Container,
  Group,
  Space,
  Text,
} from "@mantine/core";
import {modals} from "@mantine/modals";
import className from "classnames";
import dayjs from "dayjs";
import {isEmpty, noop, uniq} from "lodash";
import Link from "next/link";
import React, {useEffect, useMemo, useState} from "react";
import useResizeAware from "react-resize-aware";

import classes from "./FacilityUnitsTable.module.scss";
import * as constants from "../../constants";
import {DEFAULT_SLOTS_DAYS} from "../../constants";
import * as mutations from "../../graphql/mutations";
import * as queries from "../../graphql/queries";
import * as subscriptions from "../../graphql/subscriptions";
import type {
  Facility,
  MapSearchInput,
  SearchResultType,
  Unit,
} from "../../graphql/types";
import {SlotStatus} from "../../graphql/types";
import {getLogger} from "../../lib/logging";
import {searchStateToQueryString} from "../../lib/search";
import WatchButton from "../buttons/WatchButton";
import {useAuth} from "../contexts/AuthContext";
import HelpLink from "../HelpLink";
import Loader from "../Loader";
import MapView from "../MapView";
import {show as showCombinedSignIn} from "../modals/CombinedSignIn/CombinedSignIn";
import facilityClasses from "../popovers/FacilityPopover.module.scss";
import Slot from "../Slot";
import Tutorial from "../Tutorial";

const log = getLogger(__filename);

const SLOT_LEGEND: [SlotStatus, string][] = [
  [SlotStatus.Available, "Available to reserve"],
  [SlotStatus.Reserved, "Reserved by someone else"],
  // [SlotStatus.NotReservable, "Walk-in only"],
  // [SlotStatus.Unreleased, "Unreleased canceled reservation"],
  // [SlotStatus.Lottery, "Lottery only"],
  //[SlotStatus.Closed, "Camp site closed"],
  [SlotStatus.Unavailable, "Unavailable or unknown"],
];

export interface Props {
  facility: Partial<Facility>; // Facility to get units for
  units?: Partial<Unit>[]; // If initial units were queried beforehand, then pre-cache them
  searchInput?: MapSearchInput; // Filter to apply to facility[id].units query
  filtersApplied?: boolean; // Should units be filtered by `searchInput`?
  linkTarget?: string; // Target for <a> links
  withMap?: boolean;
  withoutFilterEnabled?: boolean;
  embedded?: boolean;
}

/**
 * Facility units table
 *
 * Shows units and their availbility slots for a facility
 *
 * User's filter criteria may be applied to limit what units are displayed
 * Filter criteria may also be toggled with a checkbox
 */
export default function FacilityUnitsTable({
  facility: initialFacility,
  units: initialUnits,
  searchInput,
  linkTarget,
  filtersApplied,
  withMap,
  withoutFilterEnabled,
  embedded = false,
}: Props) {
  const {authentication} = useAuth();

  let lastLoopName: string | undefined | null = null;

  if (filtersApplied === undefined) {
    filtersApplied = !isEmpty(searchInput?.filter);
  }

  const [filterUnits, setFilterUnits] = useState<boolean>(
    withoutFilterEnabled ? false : filtersApplied
  );
  const [checkFacility, _checkFacilityResult] = useMutation(
    mutations.CHECK_FACILITY
  );
  const [runTutorial, setRunTutorial] = useState<boolean>(false);
  const [resizeListener, sizes] = useResizeAware();

  const [
    getFacilityDetails,
    {
      loading: facilityLoading,
      error: facilityError,
      data: {entities: {facility = undefined} = {}} = {},
    },
  ] = useLazyQuery(queries.GET_FACILITY_POPOVER);

  useEffect(() => {
    getFacilityDetails({
      variables: {
        id: initialFacility.id,
        slug: initialFacility.slug,
        filter: searchInput?.filter,
        onlyMatches: filterUnits,
      },
    }).then(() => {
      setRunTutorial(true);
    });
  }, []);

  const {error: _updatesError, data: {updates: update = undefined} = {}} =
    useSubscription(subscriptions.UPDATES, {
      // skip: authentication.status !== "SIGNED_IN",
      variables: {
        inputs: facility
          ? [
              {
                modelClass: facility.__typename,
                modelId: "" + facility.id,
              },
            ]
          : [],
      },
    });

  useEffect(() => {
    if (!update) {
      return;
    }

    const {modelId, modelClass} = update;
    if (modelClass !== "Facility") {
      return;
    }

    log.debug(`Update received. Re-fetching facility ${modelId}...`);
    getFacilityDetails({
      fetchPolicy: "network-only",
      variables: {
        id: parseInt(modelId, 10),
        filter: searchInput?.filter,
        onlyMatches: filterUnits,
      },
    });
  }, [update]);

  useEffect(() => {
    getFacilityDetails({
      fetchPolicy: "network-only",
      variables: {
        id: (facility || initialFacility).id,
        filter: searchInput?.filter,
        onlyMatches: filterUnits,
      },
    });
  }, [filterUnits]);

  const [checkingFacility, setCheckingFacility] = useState<boolean>(
    initialFacility.checkRequested || false
  );

  useEffect(() => {
    if (facility) {
      setCheckingFacility(facility.checkRequested || false);
    }
  }, [facility]);

  const {
    loading: watchesLoading,
    error: watchesError,
    data: {watches: {watchList: watches = []} = {}} = {},
  } = useQuery(queries.GET_WATCH_LIST, {
    variables: {
      status: "ACTIVE",
    },
  });

  const units = initialUnits || facility?.units || [];
  const loading = facilityLoading || watchesLoading;
  const error = facilityError || watchesError;

  // Build lookup of watched units
  const watchedUnits: Set<number> = useMemo(() => {
    const results: Set<number> = new Set();
    for (const watch of watches) {
      for (const unit of watch.units) {
        if (unit.facility?.id === facility.id) {
          results.add(unit.id);
        }
      }
    }

    return results;
  }, [watches]);

  let filterUnitsInput = null;
  if (filtersApplied && facility && !initialUnits) {
    const countPrefix = filterUnits
      ? `${units.length.toLocaleString()} of ${(
          facility.unitCount || 0
        ).toLocaleString()} `
      : "";
    filterUnitsInput = (
      <div className={facilityClasses.filterUnitsCheckbox}>
        <Checkbox
          size="xs"
          label={`Only show the ${countPrefix}camp sites that match my applied filters`}
          checked={filterUnits}
          onChange={event => setFilterUnits(event.target.checked)}
          disabled={loading}
        />
      </div>
    );
  }

  const beginDate = searchInput?.filter?.dates?.min
    ? dayjs(searchInput.filter.dates.min)
    : dayjs();
  const endDate = searchInput?.filter?.dates?.max
    ? dayjs(searchInput.filter.dates.max)
    : beginDate.add(DEFAULT_SLOTS_DAYS, "days");
  const dateFormat = "YYYY-MM-DD";

  const lastCheckedAt = facility?.lastCheckedAt
    ? dayjs(facility.lastCheckedAt)
    : null;
  const now = dayjs();
  const staleDate = now.subtract(constants.SLOT_STALE_THRESHOLD_DAYS, "days");
  const isStale = !lastCheckedAt || lastCheckedAt.isBefore(staleDate);

  let unitsBody;
  if (loading) {
    unitsBody = (
      <>
        <Space h="xl" />
        <Loader centered className={classes.loader} size="lg" />
      </>
    );
  } else if (error) {
    unitsBody = (
      <Alert
        variant="filled"
        color="red"
        icon={<FontAwesomeIcon icon={faExclamationTriangle} />}>
        {error.message}
      </Alert>
    );
  } else if (!isEmpty(units)) {
    const loopCount = uniq(
      units.map((unit: Partial<Unit>) => unit.loopName)
    ).length;
    unitsBody = units?.map((unit: Partial<Unit>) => {
      let loopHeader = null;
      if (
        loopCount > 1 &&
        ((!lastLoopName && unit.loopName) ||
          (lastLoopName && lastLoopName !== unit.loopName))
      ) {
        lastLoopName = unit.loopName;
        loopHeader = (
          <div key={`loop-${unit.loopName}`} className={classes.unitLoopRow}>
            <h1>{unit.loopName}</h1>
          </div>
        );
      }

      let peopleCapacity;
      if (unit.peopleCapacity) {
        if (unit.peopleCapacity.min && unit.peopleCapacity.max) {
          if (unit.peopleCapacity.min > unit.peopleCapacity.max) {
            peopleCapacity = `${unit.peopleCapacity.min} people max`;
          } else {
            peopleCapacity = `${unit.peopleCapacity.min}-${unit.peopleCapacity.max} people`;
          }
        } else if (unit.peopleCapacity.max) {
          peopleCapacity = `${unit.peopleCapacity.max} people max`;
        }
      }

      let slotsBody;
      if (!isEmpty(unit.slots)) {
        slotsBody = (
          <>
            {unit.slots?.map((slot, i) => (
              <Slot key={i} unit={unit} slot={slot} />
            ))}
          </>
        );
      } else {
        slotsBody = (
          <div className={classes.noSlots}>
            <FontAwesomeIcon icon={faBan} /> No{" "}
            <HelpLink slug="availability" title="availability" withoutIcon />{" "}
            data for this camp site yet.
          </div>
        );
      }

      let unitUrl = `/units/${unit.slug}`;
      if (filtersApplied) {
        unitUrl += `?${searchStateToQueryString({searchInput})}`;
      }

      return (
        <React.Fragment key={`unit-${unit.slug}`}>
          {loopHeader}

          <div className={classes.unitRow}>
            <div className={classes.unitImage}>
              {unit.primaryMedia && (
                <Link href={unitUrl} legacyBehavior passHref>
                  <a target={linkTarget}>
                    <img
                      className={classes.unitImage}
                      src={
                        unit.primaryMedia.url || constants.NOT_FOUND_IMAGE_URL
                      }
                      alt={unit.name || "Camp site photo"}
                      loading="lazy"
                    />
                  </a>
                </Link>
              )}
            </div>
            <div className={classes.unitDetailsAndSlots}>
              <div className={classes.unitDetails}>
                <Link href={unitUrl} legacyBehavior passHref>
                  <a
                    className={className("unit-link", classes.unitName)}
                    target={linkTarget}>
                    {unit.name}
                  </a>
                </Link>
                {unit.isGroup && (
                  <div className={classes.unitFact} title="Group site">
                    <Group gap="xs">
                      <FontAwesomeIcon icon={faPeopleGroup} size="sm" />
                      <span>Group</span>
                    </Group>
                  </div>
                )}
                {unit.isAdaAccessible && (
                  <div
                    className={classes.unitFact}
                    title="This camp site is ADA accessible">
                    <Group gap="xs">
                      <FontAwesomeIcon icon={faWheelchair} size="sm" />
                      <span>ADA</span>
                    </Group>
                  </div>
                )}
                {unit.petsAllowed && (
                  <div
                    className={classes.unitFact}
                    title="Pets are allowed at this camp site">
                    <Group gap="xs">
                      <FontAwesomeIcon icon={faDog} size="sm" />
                      <span>Pets OK</span>
                    </Group>
                  </div>
                )}
                {unit.firePit && (
                  <div className={classes.unitFact} title="Fire pit available">
                    <Group gap="xs">
                      <FontAwesomeIcon icon={faFire} size="sm" />
                      <span>Fire pit</span>
                    </Group>
                  </div>
                )}
                {unit.picnicTable && (
                  <div
                    className={classes.unitFact}
                    title="Picnic table available">
                    <Group gap="xs">
                      <FontAwesomeIcon icon={faUtensils} size="sm" />
                      <span>Table</span>
                    </Group>
                  </div>
                )}
                {unit.waterHookup && (
                  <div
                    className={classes.unitFact}
                    title="Water hookup available">
                    <Group gap="xs">
                      <FontAwesomeIcon icon={faFaucet} size="sm" />
                      <span>Water hookup</span>
                    </Group>
                  </div>
                )}
                {unit.sewerHookup && (
                  <div
                    className={classes.unitFact}
                    title="Sewer hookup available">
                    <Group gap="xs">
                      <FontAwesomeIcon icon={faPoop} size="sm" />
                      <span>Sewer hookup</span>
                    </Group>
                  </div>
                )}
                {peopleCapacity && (
                  <div
                    className={classes.unitFact}
                    title="This camp site has a limit on the number of people that can stay">
                    <Group gap="xs">
                      <FontAwesomeIcon icon={faPerson} size="sm" />
                      <span>{peopleCapacity}</span>
                    </Group>
                  </div>
                )}
                {!!unit.maxVehicles && (
                  <div
                    className={classes.unitFact}
                    title="This camp site has a limit on the number of vehicles that can park">
                    <Group gap="xs">
                      <FontAwesomeIcon icon={faCar} size="sm" />
                      <span>{unit.maxVehicles} vehicles max</span>
                    </Group>
                  </div>
                )}
                {unit.id && watchedUnits.has(unit.id) && (
                  <div
                    className={classes.unitFact}
                    title="You are watching this camp site">
                    <Group gap="xs">
                      <FontAwesomeIcon icon={faEye} size="sm" beat />
                      <span>Watching</span>
                    </Group>
                  </div>
                )}
              </div>
              <div className={classes.slots}>{slotsBody}</div>
            </div>
          </div>
        </React.Fragment>
      );
    });
  } else if (filtersApplied && facility?.unitCount) {
    unitsBody = (
      <div>
        <Container>
          <Center>
            <img
              className={facilityClasses.noVacancyImage}
              src="/images/no_vacancy.png"
              alt="No availability"
            />
            <div>
              <Text size="md">
                <strong>No Availability</strong>
              </Text>
              <Text size="sm">
                {(facility.unitCount || 0) == 1 ? (
                  <span>
                    The single camp site at {facility.name} isn&apos;t available
                  </span>
                ) : (
                  <span>
                    None of the {(facility.unitCount || 0).toLocaleString()}{" "}
                    camp sites at {facility.name} are available
                  </span>
                )}
                <span> matching your applied filters.</span>
              </Text>
              <Space h="lg" />
            </div>
          </Center>
        </Container>
      </div>
    );
  } else {
    unitsBody = (
      <Container>
        <Center>
          <img
            className={facilityClasses.noVacancyImage}
            src="/images/no_vacancy.png"
            alt="No availability"
          />
          <div>
            <Text size="md">
              <strong>We don&apos;t have availability data yet</strong> for camp
              sites at {facility?.name}.
            </Text>
            <Space h="md" />
            <Text size="sm">
              Interested in seeing what camp sites you can reserve here?{" "}
              <strong>Watch</strong> this campground so we can start checking it
              for you.
            </Text>
          </div>
        </Center>
      </Container>
    );
  }

  const handleCheckNow = async () => {
    const showError = (error: string): void => {
      const openErrorModal = () =>
        modals.openConfirmModal({
          title: "Error",
          children: <Text size="sm">{error}</Text>,
          labels: {confirm: "Retry", cancel: "Cancel"},
          onConfirm: () => handleCheckNow(),
          onCancel: noop,
        });

      openErrorModal();
    };

    // Validations
    let error: string | null = null;
    if (facility.checkRequested) {
      error = "Facility is currently being checked";
    } else if (facility.nextCheckRequestAvailableAt) {
      const nextCheckAt = dayjs(facility.nextCheckRequestAvailableAt);
      if (nextCheckAt.isAfter(now)) {
        error = `You may check this facility again in ${nextCheckAt.fromNow()}`;
      }
    }

    // Validation failure
    if (error) {
      showError(error);
      return;
    }

    // Request check
    setCheckingFacility(true);
    try {
      await checkFacility({
        variables: {
          facilityId: facility.id,
          beginDate: beginDate.format(dateFormat),
          endDate: endDate.format(dateFormat),
        },
      });
    } catch (e: any) {
      showError(e.message);
    }
  };

  const checkNowButton = (
    <Button
      className={classes.checkNowButton}
      size="xs"
      variant="default"
      title={
        checkingFacility
          ? "Currently checking for the latest availability..."
          : "Check for the latest availability now"
      }
      loading={checkingFacility}
      disabled={loading}
      leftSection={<FontAwesomeIcon icon={faRotate} />}
      onClick={async () => {
        if (!authentication.user) {
          showCombinedSignIn({
            mode: "signIn",
            message: "You must be signed in to check availability",
            onComplete: handleCheckNow,
          });
          return;
        }

        await handleCheckNow();
      }}>
      Check now
    </Button>
  );

  const mapViewUnits: SearchResultType[] = useMemo(() => {
    if (!withMap || isEmpty(units)) {
      // No units
      return [];
    }

    const mapViewUnits = (units as SearchResultType[]).filter(
      (result: SearchResultType) => result.location
    );
    if (isEmpty(mapViewUnits)) {
      // No units with location present
      return [];
    }

    return mapViewUnits;
  }, [units]);

  return (
    <div
      className={className(classes.facilityUnitsWrapper, {
        [classes.withMap]: !isEmpty(mapViewUnits),
      })}>
      <div
        className={className(
          "facility-units-table",
          classes.facilityUnitsTable,
          {
            [classes.embedded]: embedded,
          }
        )}>
        <div
          className={className(classes.resizeWrapper, {
            [classes.embedded]: embedded,
          })}>
          {resizeListener}

          {isStale && lastCheckedAt ? (
            <>
              <Alert
                icon={<FontAwesomeIcon icon={faCircleExclamation} />}
                title={`Availability was ${
                  lastCheckedAt
                    ? `last checked ${lastCheckedAt.fromNow()}`
                    : "never checked"
                }`}
                color="yellow"
                classNames={{message: classes.staleAlertMessage}}>
                <Text size="sm" className={classes.staleAlertMessageText}>
                  <strong>Watch</strong> this campground if you want to
                  automatically check{" "}
                  <HelpLink slug="availability" title="availability" /> more
                  frequently.
                </Text>
                {checkNowButton}
              </Alert>
              <Space h="sm" />
            </>
          ) : (
            <div>
              {!loading && (
                <>
                  {checkNowButton}
                  <Space h="sm" />
                </>
              )}
            </div>
          )}
          {filterUnitsInput}
          {unitsBody}

          {!isEmpty(units) && (
            <Tutorial
              id="facilityUnitsTable"
              run={runTutorial}
              options={{
                disableScrolling: true,
                scrollToFirstStep: false,
                disableOverlay: true,
              }}
              steps={[
                {
                  target: `.${classes.unitRow}`,
                  title: "What's available?",
                  content: (
                    <div className={classes.tutorialStep}>
                      <Text size="xs">
                        Here&apos;s what the{" "}
                        <HelpLink slug="availability">colors</HelpLink> mean:
                      </Text>
                      <Space h="sm" />
                      <div className={classes.tutorialSlotsTable}>
                        {SLOT_LEGEND.map(([status, description]) => (
                          <Group key={status}>
                            <Slot
                              unit={units[0]}
                              slot={{
                                status: status,
                                slotDate: dayjs().format("YYYY-MM-DD"),
                                updatedAt: dayjs().format("YYYY-MM-DD"),
                                rate: {},
                                version: 0,
                              }}
                              tooltip={false}
                            />
                            <Text size="sm">{description}</Text>
                          </Group>
                        ))}
                      </div>
                    </div>
                  ),
                  disableBeacon: false,
                },
                {
                  target: `.${classes.checkNowButton}`,
                  title: "Check now",
                  content: (
                    <div className={classes.tutorialStep}>
                      <Text size="md">
                        <strong>
                          Click here to get the latest availability schedule
                        </strong>{" "}
                        if it&apos;s outdated or missing.
                      </Text>
                      <Space h="md" />
                      <Text size="sm">
                        We don&apos;t check schedules all the time unless you
                        want us to.
                      </Text>
                      <Space h="md" />
                      <Text size="sm">
                        We can also automatically check and optionally alert you
                        if you when something becomes available when you{" "}
                        <HelpLink slug="watches" title="watch" /> it instead.
                      </Text>
                    </div>
                  ),
                },
              ]}
            />
          )}

          {embedded && !loading && facility && (
            <div>
              <Space h="lg" />
              <Text size="sm">
                <strong>Different sites may become available.</strong>
                <br />
                <WatchButton target={facility} size="xs" /> this campground to
                get notified.
              </Text>
            </div>
          )}
        </div>
      </div>

      {!!unitsBody && !isEmpty(mapViewUnits) && (
        <div className={classes.mapViewContainer}>
          <MapView
            embedded
            height={sizes?.height}
            resultList={mapViewUnits.map((result: SearchResultType) => ({
              id: result.id + "",
              type: result.__typename || "",
              name: result.name,
              location: result.location,
              isMatch: true,
              isShared: result.isShared,
              isWatched: result.isWatched,
              result: result,
            }))}
          />
        </div>
      )}
    </div>
  );
}
